<template>
  <div>
    <NuxtRouteAnnouncer />
    <NuxtLoadingIndicator color="var(--brand-ink)" />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <AppToast />
    <AdminFontSizeSlider />
  </div>
</template>

<script setup lang="ts">
const seoQuery = useSeoSettings()
const { data: seo } = seoQuery
const designQuery = useDesignSettings()
const { locale } = useI18n()

// Admin-adjustable root font-size, persisted in a cookie so it applies on SSR.
const { fontSize } = useFontSize()

// Resolve global settings during SSR so logo + SEO land in the server-rendered
// HTML (and dehydrate to the client). retry:0 + catch so a backend hiccup never
// 500s the page.
if (import.meta.server) {
  await Promise.allSettled([seoQuery.suspense(), designQuery.suspense()])
}

// String template (with %s placeholder) keeps the head payload serializable.
const titleTemplate = computed(() => {
  const prefix = seo.value?.prefix ? `${seo.value.prefix.trimEnd()} ` : ''
  const suffix = seo.value?.suffix ? ` ${seo.value.suffix.trimStart()}` : ''
  return `${prefix}%s${suffix}`
})
// undefined (ne '') — prázdný <meta description> se nesmí vykreslit vůbec.
const description = computed(() => seo.value?.description || undefined)

// Soubory ve `public/` Nuxt kopíruje beze změny názvu (hashují se jen assety
// procházející bundlerem), takže výměna ikonky by se u vracejících se návštěvníků
// neprojevila. Při KAŽDÉ změně souborů v public/favicon/ nebo public/favicon.ico
// zvyš tohle číslo — a stejnou hodnotu přepiš i v `src` ikon v
// public/site.webmanifest, jinak zůstanou staré PWA ikonky.
const FAVICON_VERSION = 6

// Canonical + og:locale/og:url z i18n modulu. `lang` řešíme vlastním bindingem
// níž (spolu s font-size stylem), proto lang: false.
const i18nHead = useLocaleHead({ seo: true, lang: false, dir: false })
// Web běží jen v češtině — /en/* i /de/* 301ují na cs (redirect-locales-to-cs
// middleware). Hreflang alternates proto nedávají smysl (a inzerovaly by
// redirecty) — necháváme jen canonical. Při znovuzapnutí locale filtr povol.
const i18nLinks = computed(() =>
  (i18nHead.value.link || []).filter((l) => l.rel !== 'alternate')
)
const i18nMeta = computed(() =>
  (i18nHead.value.meta || []).filter((m) => m.property !== 'og:locale:alternate')
)

useHead({
  htmlAttrs: {
    lang: locale,
    style: computed(() => `font-size: ${fontSize.value}px`),
  },
  titleTemplate,
  link: computed(() => [
    { rel: 'icon', href: `/favicon.ico?v=${FAVICON_VERSION}`, sizes: '48x48' },
    { rel: 'icon', type: 'image/svg+xml', href: `/favicon/favicon.svg?v=${FAVICON_VERSION}` },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '96x96',
      href: `/favicon/favicon-96x96.png?v=${FAVICON_VERSION}`,
    },
    { rel: 'apple-touch-icon', href: `/favicon/apple-touch-icon.png?v=${FAVICON_VERSION}` },
    { rel: 'manifest', href: `/site.webmanifest?v=${FAVICON_VERSION}` },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    ...i18nLinks.value,
  ]),
  meta: computed(() => [
    // Barva chromu prohlížeče — drž v syncu s theme_color v site.webmanifest.
    { name: 'theme-color', content: '#776d64' },
    ...i18nMeta.value,
  ]),
})

useSeoMeta({
  description,
  ogDescription: description,
  ogType: 'website',
  ogSiteName: 'MD CLUB',
  ogImage: useDefaultOgImage(),
  twitterCard: 'summary_large_image',
})

// Keep global toasts below the site header. Header height differs per layout
// (public hero overlay vs. sticky member bar), so publish the live header
// bottom as a CSS var consumed by `.p-toast` in main.css. Re-measured on route
// change + resize; falls back to a static offset before mount.
if (import.meta.client) {
  const route = useRoute()
  let ro: ResizeObserver | null = null
  const measure = () => {
    const header = document.querySelector('header')
    const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0
    document.documentElement.style.setProperty('--toast-top', `${Math.max(bottom, 0) + 12}px`)
  }
  const attach = () => {
    ro?.disconnect()
    const header = document.querySelector('header')
    if (header) {
      ro = new ResizeObserver(measure)
      ro.observe(header)
    }
    measure()
  }
  onMounted(() => {
    attach()
    window.addEventListener('resize', measure)
  })
  watch(
    () => route.fullPath,
    () => nextTick(attach)
  )
  onBeforeUnmount(() => {
    ro?.disconnect()
    window.removeEventListener('resize', measure)
  })
}
</script>
